import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import OrderStatusCard from '../../order-intelligence-center/components/OrderStatusCard';
import OrderFilters from '../../order-intelligence-center/components/OrderFilters';
import LiveOrderFeed from '../../order-intelligence-center/components/LiveOrderFeed';
import OrderStatusChart from '../../order-intelligence-center/components/OrderStatusChart';
import RefundRequestsPanel from '../../order-intelligence-center/components/RefundRequestsPanel';
import OrderAnalyticsTable from '../../order-intelligence-center/components/OrderAnalyticsTable';

const OrderIntelligenceTab = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    timeRange: 'today'
  });

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleSearch = (searchTerm) => {
    console.log('Searching for:', searchTerm);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
  };

  const handleExport = async () => {
    // Simulate export
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('Export completed');
  };

  const handleBulkUpdate = () => {
    console.log('Bulk update orders');
  };

  const handleSendNotifications = () => {
    console.log('Send order notifications');
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="flex items-center justify-between">
            <div className="h-6 bg-muted rounded w-1/4"></div>
            <div className="flex gap-2">
              <div className="h-8 bg-muted rounded w-20"></div>
              <div className="h-8 bg-muted rounded w-20"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)]?.map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-lg"></div>
            ))}
          </div>
          <div className="h-96 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-background">
      <div className="p-6 space-y-6">
        {/* Tab Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">Order Intelligence</h2>
            <p className="text-sm text-muted-foreground">
              Real-time transaction monitoring and order verification for marketplace operations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkUpdate}
              className="flex items-center gap-2"
            >
              <Icon name="Edit" size={14} />
              Bulk Update
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendNotifications}
              className="flex items-center gap-2"
            >
              <Icon name="Bell" size={14} />
              Notifications
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={14} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="flex items-center gap-2"
            >
              <Icon name="Download" size={14} />
              Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <OrderFilters 
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
        />

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <OrderStatusCard
            title="Total Orders Today"
            value="1,839"
            change="+12.5%"
            changeType="positive"
            icon="ShoppingCart"
            color="primary"
          />
          <OrderStatusCard
            title="Completion Rate"
            value="94.2%"
            change="+2.1%"
            changeType="positive"
            icon="CheckCircle"
            color="success"
          />
          <OrderStatusCard
            title="Avg Fulfillment Time"
            value="2h 15m"
            change="-8m"
            changeType="positive"
            icon="Clock"
            color="warning"
          />
          <OrderStatusCard
            title="Dispute Rate"
            value="1.3%"
            change="+0.2%"
            changeType="negative"
            icon="AlertTriangle"
            color="error"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Live Order Feed */}
          <div className="lg:col-span-8">
            <LiveOrderFeed />
          </div>

          {/* Right Panel */}
          <div className="lg:col-span-4 space-y-6">
            <OrderStatusChart />
            <RefundRequestsPanel />
          </div>
        </div>

        {/* Order Analytics Table */}
        <OrderAnalyticsTable />
      </div>
    </div>
  );
};

export default OrderIntelligenceTab;